import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { fmtCAD } from "../lib/cataley";
import GoalCelebration from "../components/cataley/GoalCelebration";
import DreamImagePicker from "../components/cataley/DreamImagePicker";
import RewardsShop from "../components/cataley/RewardsShop";
import WeeklyChallenge from "../components/cataley/WeeklyChallenge";

const API = `${process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "")}/api`;

const POT_META = {
  aujourdhui: { label: "Aujourd'hui", color: "#f59e0b", emoji: "🌞" },
  projets: { label: "Mes projets", color: "#6366f1", emoji: "🌱" },
  partager: { label: "Partager", color: "#ec4899", emoji: "💝" },
};
const NAV = [
  { id: "jardin", label: "Mon jardin", emoji: "🌻" },
  { id: "dojo", label: "Mon aide", emoji: "🥋" },
  { id: "pots", label: "Mes pots", emoji: "🫙" },
  { id: "reves", label: "Mes rêves", emoji: "⭐" },
  { id: "jeux", label: "Je joue", emoji: "🎲" },
  { id: "cadeaux", label: "Mes cadeaux", emoji: "🎁" },
];

export default function CataleyEnfant() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [view, setView] = useState("jardin");

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/cataley/public/${token}`); setData(r.data); }
    catch { setErr(true); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const addStars = async (n) => {
    try { const r = await axios.post(`${API}/cataley/public/${token}/stars`, { game: "jeu", stars: n }); setData((d) => ({ ...d, stars: r.data.stars })); } catch { /* ignore */ }
  };

  // 🎉 Coffre d'objectif atteint : premier rêve à 100 % jamais fêté (figé dans un état pour survivre à la mise à jour des données)
  const [dismissed, setDismissed] = useState(new Set());
  const [celebGoal, setCelebGoal] = useState(null);
  useEffect(() => {
    if (celebGoal) return;
    const g = (data?.goals || []).find((x) => x.percent >= 100 && !x.celebrated_at && !dismissed.has(x.id));
    if (g) setCelebGoal(g);
  }, [data, dismissed, celebGoal]);
  const celebrate = useCallback(async () => {
    if (!celebGoal) return null;
    try {
      const r = await axios.post(`${API}/cataley/public/${token}/goals/${celebGoal.id}/celebrate`);
      setData((d) => ({ ...d, stars: r.data.stars, goals: d.goals.map((g) => (g.id === celebGoal.id ? { ...g, celebrated_at: "now" } : g)),
        main_goal: d.main_goal?.id === celebGoal.id ? { ...d.main_goal, celebrated_at: "now" } : d.main_goal }));
      return r.data;
    } catch { return null; }
  }, [token, celebGoal]);
  const setGoalImage = (gid, url) => setData((d) => ({ ...d, goals: d.goals.map((g) => (g.id === gid ? { ...g, image_url: url } : g)), main_goal: d.main_goal?.id === gid ? { ...d.main_goal, image_url: url } : d.main_goal }));
  const closeCelebration = () => { setDismissed((s) => new Set([...s, celebGoal.id])); setCelebGoal(null); };

  if (err) return <div className="grid min-h-screen place-items-center bg-amber-50 p-6 text-center text-stone-600">🌼 Ce lien n'est plus valide. Demande un nouveau lien à papa.</div>;
  if (!data) return <div className="grid min-h-screen place-items-center bg-amber-50 text-stone-500">Chargement…</div>;

  const showPartager = data.settings?.partager_enabled !== false;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-emerald-50 to-amber-50 pb-28" data-testid="cataley-child">
      <header className="bg-gradient-to-br from-emerald-500 to-teal-500 px-5 pb-6 pt-8 text-white shadow-lg">
        <div className="mx-auto max-w-lg">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] opacity-90" data-testid="cataley-brand">💎 Le Trésor de Cataleya Capital</div>
          <h1 className="text-3xl font-black">Bonjour {data.child_name} ! 👋</h1>
          <div className="text-sm opacity-90">{data.subtitle}</div>
          <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 text-sm font-semibold">
            ⭐ {data.stars} étoile{data.stars > 1 ? "s" : ""}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 pt-5">
        {view === "jardin" && <><WeeklyChallenge token={token} onStars={(n) => setData((d) => ({ ...d, stars: n }))} /><Jardin data={data} /></>}
        {view === "dojo" && <Dojo data={data} />}
        {view === "pots" && <Pots data={data} showPartager={showPartager} />}
        {view === "reves" && <Reves data={data} token={token} onImage={setGoalImage} />}
        {view === "jeux" && <Jeux data={data} onWin={addStars} />}
        {view === "cadeaux" && <RewardsShop token={token} stars={data.stars} onStars={(n) => setData((d) => ({ ...d, stars: n }))} />}
      </main>

      {celebGoal && <GoalCelebration goal={celebGoal} onOpen={celebrate} onClose={closeCelebration} />}

      {/* Barre de navigation */}
      <nav className="fixed inset-x-0 bottom-0 z-10 border-t border-stone-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-lg">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setView(n.id)} data-testid={`cataley-nav-${n.id}`}
              className={`flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-semibold transition-colors ${view === n.id ? "text-emerald-600" : "text-stone-400"}`}>
              <span className="text-xl">{n.emoji}</span>{n.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

function Card({ children, className = "" }) {
  return <div className={`rounded-3xl bg-white p-5 shadow-sm ${className}`}>{children}</div>;
}

function Jardin({ data }) {
  const g = data.main_goal;
  return (
    <div className="space-y-4">
      <Card className="text-center">
        <div className="text-sm font-semibold text-stone-500">Mon argent disponible</div>
        <div className="text-5xl font-black text-emerald-600" data-testid="cataley-child-available">{fmtCAD(data.available_cents)}</div>
      </Card>
      {g && (
        <Card>
          {g.image_url && <img src={g.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${g.image_url}` : g.image_url} alt="" className="mb-3 h-40 w-full rounded-2xl object-cover" data-testid="cataley-main-goal-image" />}
          <div className="text-sm font-semibold text-stone-500">🌱 Mon grand rêve</div>
          <div className="text-xl font-bold text-stone-800">{g.title}</div>
          <div className="mt-2 h-4 w-full overflow-hidden rounded-full bg-stone-100">
            <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-indigo-500 transition-all" style={{ width: `${g.percent}%` }} />
          </div>
          <div className="mt-1 text-sm text-stone-600">Il me reste <strong>{fmtCAD(g.remaining_cents)}</strong> pour l'atteindre !</div>
        </Card>
      )}
      {data.next_activity && (
        <Card>
          <div className="text-sm font-semibold text-stone-500">📅 Ma prochaine aide</div>
          <div className="font-bold text-stone-800">{data.next_activity.date} — {data.next_activity.description}</div>
          <div className="text-sm text-stone-500">Montant prévu : {fmtCAD(data.next_activity.planned_cents)}</div>
        </Card>
      )}
      {data.parent_message && (
        <Card className="bg-amber-50">
          <div className="text-sm font-semibold text-amber-700">💛 Un mot de papa</div>
          <div className="text-stone-700">{data.parent_message}</div>
        </Card>
      )}
    </div>
  );
}

function Dojo({ data }) {
  return (
    <div className="space-y-4">
      <Card>
        <h2 className="text-lg font-black text-stone-800">🥋 Mon aide au dojo</h2>
        <p className="mt-1 text-sm text-stone-600">J'aide papa le lundi et le mercredi, 30 minutes chaque fois. Quand papa confirme que j'ai reçu l'argent, il arrive dans mes pots !</p>
      </Card>
      {data.next_activity ? (
        <Card>
          <div className="text-sm font-semibold text-stone-500">Prochaine fois</div>
          <div className="text-lg font-bold text-stone-800">{data.next_activity.date}</div>
          <div className="text-stone-600">{data.next_activity.description} · {fmtCAD(data.next_activity.planned_cents)}</div>
          <div className="mt-2 inline-block rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
            {data.next_activity.status === "confirme" ? "Confirmé — papa va remettre l'argent" : "Prévu"}
          </div>
        </Card>
      ) : <Card className="text-center text-stone-500">Aucune aide prévue pour le moment 🌼</Card>}
      <Card className="bg-emerald-50 text-center">
        <div className="text-sm text-stone-600">Argent reçu en tout</div>
        <div className="text-3xl font-black text-emerald-600">{fmtCAD(data.received_cents)}</div>
      </Card>
    </div>
  );
}

function Pots({ data, showPartager }) {
  const entries = Object.entries(POT_META).filter(([k]) => k !== "partager" || showPartager);
  return (
    <div className="space-y-4">
      <Card className="text-center">
        <div className="text-sm text-stone-500">À répartir avec papa</div>
        <div className="text-3xl font-black text-amber-500">{fmtCAD(data.unallocated_cents)}</div>
      </Card>
      {entries.map(([k, m]) => (
        <Card key={k} className="flex items-center justify-between" >
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-2xl text-2xl" style={{ background: m.color + "22" }}>{m.emoji}</div>
            <div className="font-bold text-stone-700">{m.label}</div>
          </div>
          <div className="text-2xl font-black" style={{ color: m.color }} data-testid={`cataley-child-pot-${k}`}>{fmtCAD(data.pots[k])}</div>
        </Card>
      ))}
      <p className="px-2 text-center text-xs text-stone-500">Pour déplacer de l'argent entre les pots, demande à papa 💛</p>
    </div>
  );
}

function Reves({ data, token, onImage }) {
  const [picking, setPicking] = useState(null);
  const abs = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
  return (
    <div className="space-y-4">
      {(!data.goals || data.goals.length === 0) && <Card className="text-center text-stone-500">Tu n'as pas encore de rêve. Choisis-en un avec papa ! ⭐</Card>}
      {(data.goals || []).map((g) => (
        <Card key={g.id} data-testid={`cataley-child-goal-${g.id}`}>
          {g.image_url && <img src={abs(g.image_url)} alt="" className="mb-3 h-36 w-full rounded-2xl object-cover" data-testid={`cataley-goal-image-${g.id}`} />}
          <div className="flex items-center gap-3">
            <button onClick={() => setPicking(g)} data-testid={`cataley-goal-pick-image-${g.id}`} aria-label="Choisir une image"
              className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-xl bg-indigo-100 text-2xl transition-transform active:scale-95">
              {g.image_url ? <img src={abs(g.image_url)} alt="" className="h-full w-full object-cover" /> : "🖼️"}
            </button>
            <div className="flex-1">
              <div className="font-bold text-stone-800">{g.title}</div>
              <div className="text-xs text-stone-500">Prix : {fmtCAD(g.target_cents)} · <button onClick={() => setPicking(g)} className="font-semibold text-indigo-600 underline">{g.image_url ? "changer l'image" : "choisir une image"}</button></div>
            </div>
          </div>
          <div className="mt-3 h-4 w-full overflow-hidden rounded-full bg-stone-100">
            <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-indigo-500" style={{ width: `${g.percent}%` }} />
          </div>
          <div className="mt-1 flex justify-between text-sm">
            <span className="font-semibold text-indigo-600">{g.percent}%</span>
            {g.percent >= 100 ? <span className="font-bold text-amber-600" data-testid={`cataley-goal-done-${g.id}`}>🎉 Rêve atteint !</span> : <span className="text-stone-600">reste {fmtCAD(g.remaining_cents)}</span>}
          </div>
        </Card>
      ))}
      {picking && <DreamImagePicker token={token} goal={picking} onClose={() => setPicking(null)} onChosen={(url) => { onImage(picking.id, url); setPicking(null); }} />}
    </div>
  );
}

// ───────────────────────── Jeux (3 jeux de maths, données fictives) ─────────────────────────
function Jeux({ data, onWin }) {
  const [game, setGame] = useState(null);
  const diff = data.settings?.difficulty || "facile";
  if (game === "addition") return <GameAddition diff={diff} back={() => setGame(null)} onWin={onWin} />;
  if (game === "monnaie") return <GameMonnaie diff={diff} back={() => setGame(null)} onWin={onWin} />;
  if (game === "manque") return <GameManque diff={diff} back={() => setGame(null)} onWin={onWin} />;
  return (
    <div className="space-y-3">
      <Card><h2 className="text-lg font-black text-stone-800">🎲 Je joue et j'apprends</h2><p className="text-sm text-stone-600">Ces jeux utilisent des exemples pour s'amuser. Ils ne touchent jamais ton vrai argent.</p></Card>
      {[
        { id: "addition", t: "➕ Additionner des prix", d: "Combien ça fait en tout ?" },
        { id: "monnaie", t: "💵 Calculer la monnaie", d: "Combien on me redonne ?" },
        { id: "manque", t: "🎯 Combien il me manque ?", d: "Pour atteindre mon rêve" },
      ].map((x) => (
        <button key={x.id} onClick={() => setGame(x.id)} data-testid={`cataley-game-${x.id}`}
          className="w-full rounded-3xl bg-white p-5 text-left shadow-sm transition-transform hover:scale-[1.02]">
          <div className="text-lg font-bold text-stone-800">{x.t}</div>
          <div className="text-sm text-stone-500">{x.d}</div>
        </button>
      ))}
    </div>
  );
}

function rnd(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min; }
function diffMax(diff) { return diff === "difficile" ? 20 : diff === "moyen" ? 10 : 5; }

function GameShell({ title, back, question, answer, onCorrect, hint }) {
  const [val, setVal] = useState("");
  const [state, setState] = useState("ask"); // ask | good | bad
  const check = () => {
    if (Number(val.replace(",", ".")) === answer) { setState("good"); onCorrect(); }
    else setState("bad");
  };
  const next = () => { setVal(""); setState("ask"); back("new"); };
  return (
    <div className="space-y-4">
      <button onClick={() => back()} className="text-sm font-semibold text-stone-500">← Retour aux jeux</button>
      <Card>
        <div className="text-sm font-semibold text-emerald-600">{title}</div>
        <div className="mt-2 text-xl font-bold text-stone-800">{question}</div>
        <input value={val} onChange={(e) => setVal(e.target.value)} inputMode="decimal" data-testid="cataley-game-input"
          className="mt-3 w-full rounded-xl border-2 border-stone-200 px-4 py-3 text-center text-2xl font-black focus:border-emerald-500 focus:outline-none" placeholder="?" />
        {state === "ask" && <button onClick={check} data-testid="cataley-game-check" className="mt-3 w-full rounded-xl bg-emerald-600 py-3 font-bold text-white">Vérifier</button>}
        {state === "good" && (
          <div className="mt-3 rounded-xl bg-emerald-50 p-3 text-center">
            <div className="text-lg font-black text-emerald-600">Bravo ! ⭐ +1 étoile</div>
            <button onClick={next} className="mt-2 w-full rounded-xl bg-emerald-600 py-2 font-bold text-white" data-testid="cataley-game-next">Encore !</button>
          </div>
        )}
        {state === "bad" && (
          <div className="mt-3 rounded-xl bg-amber-50 p-3 text-center text-stone-700">
            Presque ! {hint}
            <button onClick={() => setState("ask")} className="mt-2 w-full rounded-xl bg-amber-500 py-2 font-bold text-white">Réessayer</button>
          </div>
        )}
      </Card>
    </div>
  );
}

function GameAddition({ diff, back, onWin }) {
  const [seed, setSeed] = useState(0);
  const m = diffMax(diff);
  const a = rnd(1, m) * 0.5, b = rnd(1, m) * 0.5;
  const key = `${a}-${b}-${seed}`;
  return <GameShell key={key} title="➕ Additionner des prix" back={(n) => n ? setSeed((s) => s + 1) : back()}
    question={`${a.toFixed(2).replace(".", ",")} $ + ${b.toFixed(2).replace(".", ",")} $ = ?`} answer={Math.round((a + b) * 100) / 100}
    onCorrect={() => onWin(1)} hint={`Additionne ${a.toFixed(2)} et ${b.toFixed(2)}.`} />;
}
function GameMonnaie({ diff, back, onWin }) {
  const [seed, setSeed] = useState(0);
  const m = diffMax(diff);
  const prix = rnd(1, m) * 0.5, paye = Math.ceil(prix) + rnd(0, 2);
  const key = `${prix}-${paye}-${seed}`;
  return <GameShell key={key} title="💵 Calculer la monnaie" back={(n) => n ? setSeed((s) => s + 1) : back()}
    question={`Le jouet coûte ${prix.toFixed(2).replace(".", ",")} $. Je paie ${paye} $. Combien on me redonne ?`} answer={Math.round((paye - prix) * 100) / 100}
    onCorrect={() => onWin(1)} hint={`Fais ${paye} − ${prix.toFixed(2)}.`} />;
}
function GameManque({ diff, back, onWin }) {
  const [seed, setSeed] = useState(0);
  const m = diffMax(diff);
  const but = rnd(4, m + 10), jai = rnd(1, but - 1);
  const key = `${but}-${jai}-${seed}`;
  return <GameShell key={key} title="🎯 Combien il me manque ?" back={(n) => n ? setSeed((s) => s + 1) : back()}
    question={`Mon rêve coûte ${but} $. J'ai déjà ${jai} $. Combien il me manque ?`} answer={but - jai}
    onCorrect={() => onWin(1)} hint={`Fais ${but} − ${jai}.`} />;
}
